import { createAssignment } from "@/api/common";
import { Button } from "@/components/ui/button";
import { showSuccessToast } from "@/lib/toast";
import { removeEmptyOrNull } from "@/lib/utils";
import { Assignment } from "@/types/assignments.types";
import { ILesson } from "@/types/lesson.types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format, formatISO, parseISO, sub } from "date-fns";
import { Formik } from "formik";
import * as Yup from "yup";
import SimilarFields from "./SimilarFields";

type CreateProps = {
  lesson?: ILesson;
  closeModal: () => void;
};
type EditProps = {
  assignment: Assignment;
  closeModal: () => void;
};

const AddAssignment = ({ lesson, closeModal }: CreateProps) => {
  const queryClient = useQueryClient();

  const initialValues = {
    lesson_id: lesson?.id ?? "",
    topic: lesson?.topic ?? "",
    content: "",
    attachments: null,
    due_date: new Date(),
  };
  const validationSchema = Yup.object().shape({
    topic: Yup.string().required("This field is required"),
    content: Yup.string(),
    due_date: Yup.date().min(
      sub(new Date(), { days: 1 }),
      "Due date cannot be before today",
    ),
  });

  const { mutate, isLoading } = useMutation(createAssignment, {
    onSuccess: ({ message }) => {
      showSuccessToast("Assignment created successfully");
      queryClient.invalidateQueries({
        queryKey: ["lesson"],
      });
      queryClient.invalidateQueries({
        queryKey: ["assignment"],
      });
      closeModal();
    },
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={({ due_date, ...rest }) =>
        mutate(
          removeEmptyOrNull({
            ...rest,
            due_date: formatISO(due_date),
          }),
        )
      }
    >
      {({ handleSubmit }) => (
        <form onSubmit={handleSubmit} className="grid gap-4">
          <SimilarFields lesson={lesson} />
          <div className="flex items-center gap-3 ml-auto">
            <Button type="button" variant="outline">
              Cancel
            </Button>
            <Button
              disabled={isLoading}
              type="submit"
              className="min-w-[100px]"
            >
              Save
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

export const EditAssignment = ({ assignment, closeModal }: EditProps) => {
  const queryClient = useQueryClient();

  const initialValues = {
    lesson_id: assignment?.id,
    topic: assignment?.topic ?? "",
    content: assignment?.content ?? "",
    attachments: null,
    due_date: format(parseISO(assignment?.dueDate), "yyyy-MM-dd"),
  };
  const validationSchema = Yup.object().shape({
    topic: Yup.string().required("This field is required"),
    content: Yup.string(),
    due_date: Yup.date().min(
      sub(new Date(), { days: 1 }),
      "Due date cannot be before today",
    ),
  });

  const { mutate, isLoading } = useMutation(createAssignment, {
    onSuccess: ({ message }) => {
      showSuccessToast("Assignment edited successfully");
      queryClient.invalidateQueries({
        queryKey: ["lesson"],
      });
      queryClient.invalidateQueries({
        queryKey: ["assignment"],
      });
      closeModal();
    },
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={({ due_date, ...rest }) =>
        mutate(
          removeEmptyOrNull({
            ...rest,
            due_date: formatISO(due_date),
          }) as any,
        )
      }
    >
      {({ handleSubmit }) => (
        <form onSubmit={handleSubmit} className="grid gap-4">
          <SimilarFields />
          <div className="flex items-center gap-3 ml-auto">
            <Button type="button" variant="outline">
              Cancel
            </Button>
            <Button
              disabled={isLoading}
              type="submit"
              className="min-w-[100px]"
            >
              Save
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default AddAssignment;
